/**
 * My Training inbox API client (Step 24).
 *
 * Wraps GET ``/api/v1/training/my-training`` (Step 19). Returns the
 * camelCase domain shape; snake_case → camelCase happens here.
 *
 * Also hosts the per-progress mutation endpoints (sign-off, etc.) so
 * that the InstructorSignoffPanel and future evidence-attestation
 * panels go through the standard ``AuthenticatedApiClient`` machinery
 * (auth-header refresh, 401-retry, error envelope normalisation) rather
 * than calling raw ``fetch``.
 */

import {AuthenticatedApiClient} from '@/matic/lib/api/authenticated-api-client';
import type {
  InboxCurriculumEntry,
  InboxCurriculumEntryResponse,
  InboxProgramEntry,
  InboxProgramEntryResponse,
  MyTrainingInbox,
  MyTrainingInboxResponse,
} from '../types/inbox';

/**
 * Sort modes for the My Training inbox.
 *
 * - ``due_date_asc`` / ``due_date_desc``: by due date (server-side default).
 * - ``assigned_desc`` / ``assigned_asc``: by ``assigned_at`` (client-side).
 * - ``status_active_first`` / ``status_completed_first``: by completion bucket
 *   (client-side; ties broken by due date).
 *
 * The ``due_date_*`` modes are forwarded to the backend; the rest are
 * applied client-side after fetch. Persisted in localStorage via
 * ``useInboxSortPreference``.
 */
export type InboxSort =
  | 'due_date_asc'
  | 'due_date_desc'
  | 'assigned_desc'
  | 'assigned_asc'
  | 'status_active_first'
  | 'status_completed_first';

/** Backend-supported sort modes — the only ones forwarded to ``GET /my-training?sort=``. */
export type InboxBackendSort = 'due_date_asc' | 'due_date_desc';

/**
 * Sign-off command — re-auth credentials + attestation note.
 * ``mfaCode`` is forwarded only when the tenant requires MFA.
 */
export type SignOffTrainingProgressInput = {
  password: string;
  mfaCode?: string;
  attestationNote: string;
};

export type SignOffTrainingProgressResult = {
  progressId: string;
  signedAt: string;
};

/** Pre-signed URL + file metadata for an external-evidence upload. */
export type ExternalEvidenceFileUrl = {
  url: string;
  filename: string;
  contentType: string;
  expiresIn: number;
};

type ExternalEvidenceFileUrlResponse = {
  url: string;
  filename: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  content_type: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  expires_in: number;
};

type SignOffResponse = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  progress_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  signed_at: string;
};

/**
 * Pending-secondary-attestations inbox row (F-1 follow-up).
 *
 * One entry per EXTERNAL_EVIDENCE progress row awaiting the calling
 * user's secondary attestation. Surfaces enough context for the
 * inbox UI to render program name, evidence label, assignee name, and
 * the uploaded file metadata without follow-up calls.
 */
export type PendingSecondaryAttestation = {
  progressId: string;
  programItemId: string;
  trainingAssignmentId: string;
  trainingProgramId: string;
  trainingProgramName: string;
  assigneeUserId: string;
  assigneeUserName?: string;
  evidenceLabel: string;
  uploadedFileId: string;
  uploadedAt: string;
  expiryDate?: string;
  awaitingAttestationSince: string;
};

type PendingSecondaryAttestationResponseItem = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  progress_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  program_item_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  training_assignment_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  training_program_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  training_program_name: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  assignee_user_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  assignee_user_name?: string | null;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  evidence_label: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  uploaded_file_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  uploaded_at: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  expiry_date?: string | null;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  awaiting_attestation_since: string;
};

type PendingSecondaryAttestationsResponse = {
  items: PendingSecondaryAttestationResponseItem[];
};

/**
 * Pending-instructor-signoffs inbox row.
 *
 * Sibling of ``PendingSecondaryAttestation`` for live-observation
 * INSTRUCTOR_SIGNOFF items. ``itemTitle`` is the user-authored
 * program-item title; ``instructions`` is the optional configuration
 * block surfaced to instructors so they know what to look for before
 * signing. There's no upload row for live-observation steps, so the
 * "awaiting since" timestamp is the assignment's creation time.
 */
export type PendingInstructorSignoff = {
  progressId: string;
  programItemId: string;
  trainingAssignmentId: string;
  trainingProgramId: string;
  trainingProgramName: string;
  assigneeUserId: string;
  assigneeUserName?: string;
  itemTitle: string;
  instructions?: string;
  awaitingSignoffSince: string;
};

type PendingInstructorSignoffResponseItem = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  progress_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  program_item_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  training_assignment_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  training_program_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  training_program_name: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  assignee_user_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  assignee_user_name?: string | null;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  item_title: string;
  instructions?: string | null;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  awaiting_signoff_since: string;
};

type PendingInstructorSignoffsResponse = {
  items: PendingInstructorSignoffResponseItem[];
};

/**
 * Completed sign-off row — one entry per training-attestation
 * signature the calling user has performed. Backs the trainee
 * Approvals → Completed pane.
 */
export type CompletedSignoff = {
  signatureId: string;
  progressId: string;
  programItemId: string;
  trainingAssignmentId: string;
  assignedUserId: string;
  itemTitle: string;
  signedAt: string;
  meaning: string;
  reason: string;
  uploadFileId?: string;
};

type CompletedSignoffResponseItem = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  signature_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  progress_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  program_item_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  training_assignment_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  assigned_user_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  item_title: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  signed_at: string;
  meaning: string;
  reason: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  upload_file_id?: string | null;
};

type CompletedSignoffsResponse = {
  items: CompletedSignoffResponseItem[];
};

export class MyTrainingApiClient extends AuthenticatedApiClient {
  async getInbox(
    sort: InboxBackendSort = 'due_date_asc',
    includeCompletedWithinDays = 365
  ): Promise<MyTrainingInbox> {
    const params = new URLSearchParams();
    params.set('sort', sort);
    params.set(
      'include_completed_within_days',
      String(includeCompletedWithinDays)
    );
    const response = await this.get<MyTrainingInboxResponse>(
      `/api/v1/training/my-training?${params.toString()}`
    );
    return {
      curricula: response.curricula.map((c) => this.toCurriculum(c)),
      programs: response.programs.map((p) => this.toProgram(p)),
    };
  }

  /**
   * List EXTERNAL_EVIDENCE progress rows awaiting the calling user's
   * secondary attestation (F-1 follow-up).
   *
   * Filtering is implicit (caller-only) — backend reads
   * ``principal.user_id`` directly.
   */
  async listPendingSecondaryAttestations(): Promise<
    PendingSecondaryAttestation[]
  > {
    const response = await this.get<PendingSecondaryAttestationsResponse>(
      '/api/v1/training/pending-secondary-attestations'
    );
    return response.items.map((r) => ({
      progressId: r.progress_id,
      programItemId: r.program_item_id,
      trainingAssignmentId: r.training_assignment_id,
      trainingProgramId: r.training_program_id,
      trainingProgramName: r.training_program_name,
      assigneeUserId: r.assignee_user_id,
      assigneeUserName: r.assignee_user_name ?? undefined,
      evidenceLabel: r.evidence_label,
      uploadedFileId: r.uploaded_file_id,
      uploadedAt: r.uploaded_at,
      expiryDate: r.expiry_date ?? undefined,
      awaitingAttestationSince: r.awaiting_attestation_since,
    }));
  }

  /**
   * List historical sign-offs the calling user has performed. Backs
   * the trainee Approvals → Completed pane. Returned most-recent
   * first by the server.
   */
  async listMyCompletedSignoffs(): Promise<CompletedSignoff[]> {
    const response = await this.get<CompletedSignoffsResponse>(
      '/api/v1/training/my-completed-signoffs'
    );
    return response.items.map((r) => ({
      signatureId: r.signature_id,
      progressId: r.progress_id,
      programItemId: r.program_item_id,
      trainingAssignmentId: r.training_assignment_id,
      assignedUserId: r.assigned_user_id,
      itemTitle: r.item_title,
      signedAt: r.signed_at,
      meaning: r.meaning,
      reason: r.reason,
      uploadFileId: r.upload_file_id ?? undefined,
    }));
  }

  /**
   * List INSTRUCTOR_SIGNOFF progress rows awaiting the calling user's
   * primary sign-off. Sibling of
   * :func:`listPendingSecondaryAttestations` — same caller-only
   * filtering, different parent item type.
   */
  async listPendingInstructorSignoffs(): Promise<PendingInstructorSignoff[]> {
    const response = await this.get<PendingInstructorSignoffsResponse>(
      '/api/v1/training/pending-instructor-signoffs'
    );
    return response.items.map((r) => ({
      progressId: r.progress_id,
      programItemId: r.program_item_id,
      trainingAssignmentId: r.training_assignment_id,
      trainingProgramId: r.training_program_id,
      trainingProgramName: r.training_program_name,
      assigneeUserId: r.assignee_user_id,
      assigneeUserName: r.assignee_user_name ?? undefined,
      itemTitle: r.item_title,
      instructions: r.instructions ?? undefined,
      awaitingSignoffSince: r.awaiting_signoff_since,
    }));
  }

  /**
   * Trainee external-evidence upload — full three-phase flow (Gap 3).
   *
   * Phases:
   *   1. POST ``/training-progress/external-evidence/upload-url`` to
   *      reserve a staging ``file_id`` and (in S3 mode) get a PUT URL.
   *      In local-storage mode the URL field is ``null``; we re-PUT to
   *      the same URL pattern via the local storage token endpoint.
   *   2. PUT the file bytes directly to the URL.
   *   3. POST ``/training-progress/{id}/external-evidence`` with
   *      ``{file_id, expiry_date, cui}`` to bind the staged file to
   *      the progress row + transition state.
   *
   * Errors propagate as ``EnhancedApiError`` so the caller's panel can
   * surface them inline (corrupt PDF, MIME mismatch, etc.).
   */
  async uploadExternalEvidence(input: {
    progressId: string;
    file: File;
    expiryDate?: string;
    cui: boolean;
  }): Promise<void> {
    const contentType = input.file.type || 'application/octet-stream';

    // Phase 1: request the pre-signed URL + staging file_id.
    const formData = new FormData();
    formData.append('filename', input.file.name);
    formData.append('content_type', contentType);
    formData.append('file_size', String(input.file.size));
    const stagingResponse = await this.post<{
      url: string | null;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      file_id: string;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      expires_in: number;
    }>(
      '/api/v1/training/training-progress/external-evidence/upload-url',
      formData
    );

    if (!stagingResponse.url) {
      // Local-storage / token-mode without a URL means staging support
      // isn't configured — fail loudly so the caller can surface a
      // toast rather than silently dropping the upload.
      throw new Error(
        'Upload URL not available. Check storage configuration with your administrator.'
      );
    }

    // Phase 2: PUT the file bytes directly to the staging URL.
    // No auth header — the URL is its own credential (S3 presigned or
    // local storage token).
    const uploadResponse = await fetch(stagingResponse.url, {
      method: 'PUT',
      body: input.file,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: {'Content-Type': contentType},
    });
    if (!uploadResponse.ok) {
      throw new Error(
        `File upload failed (status ${String(uploadResponse.status)}).`
      );
    }

    // Phase 3: confirm the upload against the progress row. We pass
    // filename + content_type + file_size so the backend can persist
    // them on the upload row directly — evidence files don't go
    // through the document-revision-files table (090c46847742).
    /* eslint-disable @typescript-eslint/naming-convention */
    const body: Record<string, unknown> = {
      file_id: stagingResponse.file_id,
      filename: input.file.name,
      content_type: contentType,
      file_size: input.file.size,
      cui: input.cui,
    };
    /* eslint-enable @typescript-eslint/naming-convention */
    if (input.expiryDate) {
      body.expiry_date = input.expiryDate;
    }

    await this.post(
      `/api/v1/training/training-progress/${input.progressId}/external-evidence`,
      body
    );
  }

  /**
   * Get a pre-signed download URL for an uploaded external-evidence
   * file. Backend gates on admin/quality OR the assignee themselves.
   * The URL is short-lived (presigned-URL TTL) so callers should fetch
   * fresh per preview.
   */
  async getExternalEvidenceUrl(
    progressId: string
  ): Promise<ExternalEvidenceFileUrl> {
    const response = await this.get<ExternalEvidenceFileUrlResponse>(
      `/api/v1/training/training-progress/${progressId}/external-evidence/url`
    );
    return {
      url: response.url,
      filename: response.filename,
      contentType: response.content_type,
      expiresIn: response.expires_in,
    };
  }

  /**
   * Instructor sign-off (Story 9). Posts the re-auth credentials +
   * attestation note. The standard request pipeline handles auth
   * headers and surfaces error envelopes via ``EnhancedApiError`` —
   * callers should inspect ``error.status`` to distinguish 401
   * (wrong password) from 409 (already recorded) etc.
   */
  async signOffTrainingProgress(
    progressId: string,
    input: SignOffTrainingProgressInput
  ): Promise<SignOffTrainingProgressResult> {
    const body = {
      password: input.password,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      mfa_code: input.mfaCode,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      attestation_note: input.attestationNote,
    };
    const response = await this.post<SignOffResponse>(
      `/api/v1/training/training-progress/${progressId}/sign-off`,
      body
    );
    return {
      progressId: response.progress_id,
      signedAt: response.signed_at,
    };
  }

  private toProgram(r: InboxProgramEntryResponse): InboxProgramEntry {
    return {
      assignmentId: r.assignment_id,
      trainingProgramId: r.training_program_id,
      trainingProgramRevisionId: r.training_program_revision_id,
      name: r.name,
      status: r.status,
      cellStatus: r.cell_status,
      assignedAt: r.assigned_at,
      dueDate: r.due_date,
      completedAt: r.completed_at ?? undefined,
      sourceAttribution: r.source_attribution,
      programStatus: r.program_status ?? 'published',
      sourceGroupId: r.source_group_id ?? undefined,
      sourceGroupName: r.source_group_name ?? undefined,
      sourceCurriculumAssignmentId:
        r.source_curriculum_assignment_id ?? undefined,
    };
  }

  private toCurriculum(r: InboxCurriculumEntryResponse): InboxCurriculumEntry {
    return {
      curriculumAssignmentId: r.curriculum_assignment_id,
      curriculumId: r.curriculum_id,
      curriculumName: r.curriculum_name,
      status: r.status,
      cellStatus: r.cell_status,
      assignedAt: r.assigned_at,
      dueDate: r.due_date ?? undefined,
      completedAt: r.completed_at ?? undefined,
      sourceAttribution: r.source_attribution,
      curriculumStatus: r.curriculum_status ?? 'published',
      sourceGroupId: r.source_group_id ?? undefined,
      sourceGroupName: r.source_group_name ?? undefined,
      programsTotal: r.programs_total,
      programsCompleted: r.programs_completed,
      constituentPrograms: r.constituent_programs.map((p) => this.toProgram(p)),
    };
  }
}

const clientCache = new Map<string, MyTrainingApiClient>();

export function getMyTrainingApi(token?: string): MyTrainingApiClient {
  const key = token ?? 'no-token';
  let client = clientCache.get(key);
  if (!client) {
    client = new MyTrainingApiClient(token);
    clientCache.set(key, client);
  }

  return client;
}
